import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from '../../api/facades';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await authApi.requestPasswordReset({ email: trimmed });
      setSent(true);
    } catch (err: any) {
      toast.error(err?.body?.message || err?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8">
        <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
          Forgot your password?
        </h1>
        {sent ? (
          <>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              If an account exists for <span className="font-semibold">{email}</span>, a password reset link has been sent. Please check your inbox.
            </p>
            <Link
              to="/sign-in"
              className="block text-center w-full py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Enter the email address linked to your account and we'll send you a reset link.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-slate-900 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 mt-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
            <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
              Remembered it?{' '}
              <Link to="/sign-in" className="font-semibold text-primary dark:text-primary-light hover:underline">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
